import * as z from "zod";
import { creditNoteDocumentDto } from "../documents/credit-note-document";
import { decimal, pagination, version } from "../index";
import { aggregateSyncDto, MAX_LINE_UNITS, syncStatuses } from "../til";
import { currency, postalAddress } from "../til-fields";
import { movementMessages, movementStatuses } from "./enums";
import { MAX_MOVEMENT_LINES } from "./stock-movement";

// The vendor returns (VRA, FR-M12-09…11, B-54 point 2, ADR-227; 50-api/logistics.md): Logistics opens a return of
// units to their supplier, from a warehouse (the returns one, as a rule), with the supplier's address, the lines and
// their prices. The supplier confirms it in Atelier, or refuses it with the reason, and then Operations decides
// (gate `logistics.vendorReturnRefusal`). Confirmed, it goes to the warehouse (I-10) and to Business Central (I-25)
// in the same step, as every movement: a `logistics.stock_movement` of type `vendor_return`.

/** Who reads the vendor returns: Tomorrow, and the supplier its own (RLS on `supplier_org_id`). */
export const VENDOR_RETURN_READ = "vendor_return.read";
/** Logistics opens a vendor return, cancels it and sends its messages again. */
export const VENDOR_RETURN_OPEN = "vendor_return.open";
/** The supplier confirms or refuses one of its vendor returns. */
export const VENDOR_RETURN_ANSWER = "vendor_return.answer";
/** Operations decides a vendor return the supplier refused, with the role of the gate. */
export const VENDOR_RETURN_DECIDE = "vendor_return.decide";
/**
 * Finance approves the credit note of an executed vendor return (B-54 point 3, ADR-230): a credit note of a supplier,
 * as those of the missing units of an inbound (ADR-140).
 */
export const VENDOR_RETURN_CREDIT = "credit_note.manage";

/** The supplier's address the goods go back to, copied when the return is opened: I-10 `shipTo`. */
export const returnAddress = postalAddress;
export type ReturnAddress = z.output<typeof returnAddress>;

const reason = z.string().trim().min(1).max(2000);
const unitPrice = decimal.refine((v) => Number(v) >= 0, "A price is not negative");

/**
 * FR-M12-09, B-54 point 2: the warehouse the units leave, the supplier and the address of its address book they go
 * back to, the currency of the prices, why, and the lines with their units and prices. The id is the client's
 * (ADR-046): the same request again answers the return it created.
 */
export const openVendorReturnInput = z.object({
  id: z.uuid(),
  fromLocationId: z.uuid(),
  supplierOrgId: z.uuid(),
  /** An address of the supplier's address book (`party.address`). */
  addressId: z.uuid(),
  currency,
  memo: z.string().trim().min(1).max(4000),
  lines: z
    .array(
      z.object({
        skuId: z.uuid(),
        qty: z.number().int().positive().max(MAX_LINE_UNITS),
        /** The value of one unit, in `currency`: what Business Central registers and the credit note asks. */
        unitPrice,
      }),
    )
    .min(1)
    .max(MAX_MOVEMENT_LINES),
});
export type OpenVendorReturnInput = z.input<typeof openVendorReturnInput>;

export const getVendorReturnInput = z.object({ vendorReturnId: z.uuid() });
export type GetVendorReturnInput = z.input<typeof getVendorReturnInput>;
/** A vendor return by its id: the path of the routes of one return. */
export const vendorReturnParams = getVendorReturnInput.pick({ vendorReturnId: true });

/** An action on a vendor return: `version` is the one the client read (409 otherwise). */
export const vendorReturnActionInput = getVendorReturnInput.extend({ version });
export type VendorReturnActionInput = z.input<typeof vendorReturnActionInput>;
export const vendorReturnActionBody = vendorReturnActionInput.omit({ vendorReturnId: true });

/**
 * F2 (ADR-230): Finance approves the credit note of an executed vendor return, which documents asks Business Central
 * for (I-04 `credit_note`), on the units the warehouse shipped back.
 */
export type ApproveVendorReturnCreditInput = VendorReturnActionInput;

/** The supplier refuses, Operations decides a refusal, Logistics cancels: always with the reason. */
export const vendorReturnReasonInput = vendorReturnActionInput.extend({ reason });
export type VendorReturnReasonInput = z.input<typeof vendorReturnReasonInput>;
export const vendorReturnReasonBody = vendorReturnReasonInput.omit({ vendorReturnId: true });

/** One message of a confirmed vendor return, sent again as a new revision. */
export const resyncVendorReturnInput = vendorReturnActionInput.extend({ message: z.enum(movementMessages) });
export type ResyncVendorReturnInput = z.input<typeof resyncVendorReturnInput>;
export const resyncVendorReturnBody = resyncVendorReturnInput.omit({ vendorReturnId: true });

const listFilters = {
  /**
   * `requested`: waiting for the supplier; `awaiting_confirmation`: refused, waiting for Operations; `confirmed`: with
   * the warehouse and Business Central; `executed`: shipped by the warehouse; `cancelled`.
   */
  status: z.enum(movementStatuses).optional(),
  /** The warehouse the units leave. */
  location: z.uuid().optional(),
  supplier: z.uuid().optional(),
};

/** The vendor returns, the latest opened first. */
export const listVendorReturnsInput = z.object({
  ...listFilters,
  /** Part of the number of the return, case-insensitive and without separators (ADR-117). */
  q: z.string().trim().min(1).max(80).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListVendorReturnsInput = z.input<typeof listVendorReturnsInput>;

/** How many vendor returns with the same filters, and since when the oldest was opened (ADR-086): a queue of the home. */
export const countVendorReturnsInput = z.object(listFilters);
export type CountVendorReturnsInput = z.input<typeof countVendorReturnsInput>;

/** A vendor return as its list shows it. Tomorrow's fields are absent for the supplier. */
export const vendorReturnSummaryDto = z
  .object({
    id: z.uuid(),
    /** Generated by Nexxt (`VR-`, the warehouse, the day and a sequence): how every system names it. */
    vendorReturnNumber: z.string(),
    status: z.enum(movementStatuses),
    location: z.object({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      /** `reference.legal_entity.code`: the company the units leave. */
      company: z.string(),
    }),
    supplier: z.object({ id: z.uuid(), code: z.string().nullable(), name: z.string() }),
    returnAddress,
    /** Why the goods go back. */
    memo: z.string().nullable(),
    currency: z.string().nullable(),
    /** The sum of units × price of every line. */
    totalAmount: decimal,
    totalUnits: z.number().int(),
    lineCount: z.number().int(),
    requestedAt: z.iso.datetime().nullable(),
    /** When the supplier confirmed it, or Operations after its refusal. */
    confirmedAt: z.iso.datetime().nullable(),
    /** When the supplier refused it, and why. */
    supplierRefusedAt: z.iso.datetime().nullable(),
    supplierRefusalReason: z.string().nullable(),
    /** Why it was cancelled: by Logistics, or by Operations accepting the refusal. */
    cancelReason: z.string().nullable(),
    /** When its cancellation left for the two systems, which have still to answer. */
    cancelRequestedAt: z.iso.datetime().nullable().optional(),
    /** How the warehouse (I-10) and Business Central (I-25) took its last state: Tomorrow's. */
    wmsSyncStatus: z.enum(syncStatuses).optional(),
    bcSyncStatus: z.enum(syncStatuses).optional(),
    /** The number of the document Business Central registered, from the outcome of I-25. */
    bcDocumentNo: z.string().nullable().optional(),
    /** When the warehouse closed the step `shipped` (I-11): the return is executed. */
    shippedAt: z.iso.datetime().nullable(),
    /**
     * F2 (ADR-230), Tomorrow's: when Finance approved the credit note of the executed return, and the credit note
     * documents asked Business Central for.
     */
    creditApprovedAt: z.iso.datetime().nullable().optional(),
    creditNote: creditNoteDocumentDto.nullable().optional(),
    version: z.number().int(),
  })
  .meta({ id: "VendorReturnSummary" });
export type VendorReturnSummaryDto = z.output<typeof vendorReturnSummaryDto>;

/** A vendor return with its lines: alone and after an action. */
export const vendorReturnDto = vendorReturnSummaryDto
  .extend({
    requestedBy: z.uuid().nullable().optional(),
    confirmedBy: z.uuid().nullable().optional(),
    lines: z.array(
      z.object({
        id: z.uuid(),
        /** The codes copied from the catalog when the line was born: the supplier does not read the brand's catalog. */
        sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
        qty: z.number().int(),
        unitPrice: decimal.nullable(),
        /** The units free in the warehouse when the return was opened (FR-M12-04): Tomorrow's. */
        atpAtRequest: z.number().int().nullable().optional(),
        /** The units the warehouse says left (I-11). */
        qtyShipped: z.number().int(),
      }),
    ),
  })
  .meta({ id: "VendorReturn" });
export type VendorReturnDto = z.output<typeof vendorReturnDto>;

/** How a vendor return stands towards the warehouse and Business Central: one state per message (ADR-141). */
export const vendorReturnSyncDto = z
  .object({
    vendorReturnId: z.uuid(),
    messages: z.array(aggregateSyncDto.extend({ message: z.enum(movementMessages) })),
  })
  .meta({ id: "VendorReturnSync" });
export type VendorReturnSyncDto = z.output<typeof vendorReturnSyncDto>;
